import React, { useState, useEffect, useLayoutEffect } from "react";
import * as localforage from "localforage";
import moment from "dayjs";
import { authLogin } from "@/apis/loginAPI";
import { authLogout } from "@/apis/logoutAPI";
import { checkIf2FA } from "@/apis/apiCollection";
import { setToken } from "@/apis/getToken";
import { setPermissions } from "@/apis/getPermisions";
import { getHasPermissionEntry } from "@/config/drawerListItems";
import Cookies from "js-cookie";
import { setCurrentCompanyId } from "@/apis/getCurrentCompanyId";
import { setUserInfo } from "@/apis/getUserInfo";
import { isCNDomain } from "@/helpers/utils";
import { APP_ENV } from "@/APP_ENV";

export const AuthContext = React.createContext({});

export function createGtag(userId) {
  if (!isCNDomain()) {
    (function (w, d, s, l, i) {
      w[l] = w[l] || [];

      function t() {
        w[l].push(arguments);
      }

      t("js", new Date());
      t("config", i, {
        user_id: userId,
        cookie_domain: ".nubiscloud.io",
        cookie_flags: "SameSite=None;Secure"
      });
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s);
      j.async = true;
      j.src = "https://www.googletagmanager.com/gtag/js?id=" + i;
      f.parentNode.insertBefore(j, f);
    })(window, document, "script", "dataLayer", "G-944PEXY9BT");
  }
}

export default function Auth({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [canLoadLangConf, setCanLoadLangConf] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoginFailed, setIsLoginFailed] = useState(false);
  const isChinaIP = isCNDomain();
  console.log("Auth Enter",)

  useLayoutEffect(() => {
    console.log("Checking Ticket...");
    const [ticket, loginStatus, urlObj] = checkTicket();

    if (loginStatus === "true") {
      // from login pipeline, get ticket
      // ?ticket=XXX&login_status=true
      console.log("Got ticket, now get the token...");
      login(ticket, urlObj);
    } else if (loginStatus === "false") {
      // from login pipeline, something wrong at CAS <=> API server
      // ?login_status=false
      setIsLoginFailed(true);
    }
  }, []);

  const checkTicket = () => {
    /* When redirect back from API server,
            check ticket and login_status{true, false}
        */
    const urlObj = new URL(document.location);
    const params = urlObj.searchParams;
    const ticket = params.get("ticket");
    const loginStatus = params.get("login_status");

    return [ticket, loginStatus, urlObj];
  };

  const checkAuth = async here => {
    // check expired_at with current time
    // transfer current date from local time zone to utc
    console.log("Check auth..." + here);
    const currentDate = moment.utc().format("YYYY-MM-DD HH:mm:ss");
    const expiredDate = await localforage.getItem("expiredAt");
    const token = await localforage.getItem("token");
    const userPermissions = await localforage.getItem("userPermissions");
    const currentCompanyId = await localforage.getItem("currentCompanyId");
    const user = await localforage.getItem("user");

    const [, loginStatus] = checkTicket();

    if (loginStatus) {
      return;
    }

    if (!token || !expiredDate || currentDate > expiredDate) {
      // Token expired or no token
      setIsAuthenticated(false);
      setToken(null);
      setPermissions([]);
      setUserInfo({});
      setCurrentCompanyId(null);
      setIsLoading(false);
      localforage.clear();
      return;
    } else {
      setToken(token);
      setPermissions(userPermissions);
      setUserInfo(user);
      setCurrentCompanyId(currentCompanyId);
      // Is Authenticated
      setIsAuthenticated(true);
      setCanLoadLangConf(true)
      setIsLoading(false);
      return;
    }
  };

  const login = async (ticket, urlObj) => {
    // call api, store token, expired_at
    if (urlObj.pathname.includes("verify-email")) {
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    // if (process.env.NODE_ENV !== "development") {
    if (urlObj.pathname === "/") {
      const lastLogoutAt = localStorage.getItem("lastLogutAt");
      if (lastLogoutAt) {
        if (Date.now() - new Date(Number(lastLogoutAt)).getTime() < 5000) {
          localStorage.removeItem("lastLogutAt");
          authLogout();
          return;
        }
      }
      const no_2fa = process.env.REACT_APP_NO_2FA ?? Cookies.get("no_2fa");
      if (!no_2fa) {
        const token = await localforage.getItem("token");
        if (!token) {
          const res = await checkIf2FA(ticket);
          if (res.otpRequired) {
            urlObj.pathname = "verify-email/";
            window.location.href = urlObj.toString();
            return;
          }
        }
      }
    }
    // }
    // Use ticket to call API server for getting token and permissions.
    const hasLoginData = await authLogin(ticket);
    console.log("login...");

    if (!hasLoginData) {
      console.log("[Auth Error]:Can't get login Data");
      setIsLoginFailed(true);
      setIsLoading(false);
    } else {
      console.log("login success");
      const entryPath = getHasPermissionEntry();
      if (entryPath === "/") {
        setIsAuthenticated(true);
        setIsLoading(false);
        window.history.pushState(
          {},
          "",
          window.location.href.split("?")[0] + entryPath.slice(1)
        );
      } else {
        window.location.href =
          window.location.href.split("?")[0] + entryPath.slice(1);
      }
    }
  };

  const logout = async (recordLogut, casLogOut) => {
    // call Logout API
    await authLogout(recordLogut, casLogOut);
  };

  useEffect(() => {
    if (isAuthenticated) {
      localforage.getItem("user").then(user => {
        createGtag(user.email);
      });
    }
  }, [isAuthenticated]);

  return (
    // AuthContext.Provider will wrap the components
    // The wrapped components can use the value sent from the AuthContext.Provider
    // To use the value, the wrapped components should delcare 'useContext(AuthContext)'
    <AuthContext.Provider
      value={{
        isAuthenticated,
        canLoadLangConf,
        isLoading,
        isChinaIP,
        login,
        logout,
        checkAuth,
        setIsLoading,
        isLoginFailed,
        setIsLoginFailed
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
